import React from 'react';
import { Checklist } from 'src/data/types/base';
import { RuleDescriptor } from 'src/data/types/descriptor';
import { Segment, Tour } from 'src/types';
import { parseJsonSafe } from 'src/utils/core';
import { getServerSideData } from 'src/utils/server-side';
import { stripHtml } from 'src/utils/text';
import { generalOperators, generalWideOperators, getPropIds, OperatorType, PredefinedPropType, useBasicDefinitionForProp } from './definitions';
import { useSurveyBare } from 'src/utils/hooks/useSurveyBare';

export default function CollapsedRuleDescription(props: {
    rule: RuleDescriptor, allTours: Tour[], allSegments: Segment[], allChecklists?: Checklist[],
    supportsEvents?: boolean, supportsUserInteractivity?: boolean, supportsChecklists?: boolean,
    supportsSurveys?: boolean
}) {
    const exp = props.rule.expression;

    const op = exp.op;
    const val = exp.val;
    const prop = exp.prop || '';
    const [groupId, attrId] = getPropIds(prop);

    const [_, __, attrConfig] = useBasicDefinitionForProp(prop, props.allTours, props.allSegments, props.allChecklists, true, {
        supportsEvents: props.supportsEvents,
        supportsUserInteractivity: props.supportsUserInteractivity,
        supportsChecklistStates: props.supportsChecklists,
        supportsSurveys: props.supportsSurveys
    });

    const attrLabel = attrConfig.displayText || attrConfig.text;
    const attrOperators = attrConfig.operators || generalOperators;
    const operator = attrOperators.filter(i => i.value == op)[0] || {} as OperatorType;
    const operatorLabel = operator.displayText || operator.text;
    const operatorPostfixLabel = operator.inputPostfix || '';

    const surveyBare = useSurveyBare(val, attrConfig.value && attrConfig.value.startsWith('surv.'));

    if (!attrConfig.value) {
        return <div><i>...</i></div>
    } else if (attrConfig.value && attrConfig.value.startsWith('tour.')) {
        return <div>
            Tour <b>{attrConfig.opts.filter(i => i.value == val)[0]?.text || '???'}</b> {operatorLabel} <b>{attrLabel}</b>
        </div>
    } else if (attrConfig.value && attrConfig.value.startsWith('chklst.')) {
        return <div>
            Checklist <b>{attrConfig.opts.filter(i => i.value == val)[0]?.text || '???'}</b> {operatorLabel} <b>{attrLabel}</b>
        </div>
    } else if (attrConfig.value && attrConfig.value.startsWith('segment')) {
        return <div>
            Segment <b>{attrConfig.opts.filter(i => i.value == val)[0]?.text || '???'}</b> {operatorLabel} <b>{attrLabel}</b>
        </div>
    } else if (attrConfig.value && attrConfig.value.startsWith('user')) {
        const attrLabelHasWeight = attrConfig.hasValue !== false;

        const userOp = generalWideOperators.filter(i => i.value == op)[0];
        const opPostfix = userOp?.inputPostfix || '';
        const opLabel = userOp?.displayText || userOp?.text || '';
        const hasValue = userOp?.hasValue !== false ?? true;

        return <div>
            {attrLabelHasWeight ? <b>{attrLabel}</b> : <span>{attrLabel}</span>}
            <span> {opLabel} </span> {/* keep spaces around! */}
            {hasValue ? (typeof val === 'undefined' || val == '' || val == null ? <i>not set</i> : <WrappedValue>{val}</WrappedValue>) : null}
            {opPostfix && <span> {opPostfix}</span>}
        </div>
    } else if (attrConfig.value == 'event') {
        const isRefined = !!props.rule.refinedRule;

        return <div>
            Event <b>{attrId || '???'}</b> {operatorLabel} {isRefined && <i><small>+ refined</small></i>}
        </div>
    } else if (attrConfig.value && attrConfig.value.startsWith('uiact.')) {
        const data = parseJsonSafe(exp.data, { sel: '', url: '' });

        if (attrId == 'elState') {
            return <div>
                Element <b><WrappedValue>{data.sel}</WrappedValue></b> at <i><WrappedValue>{data.url}</WrappedValue></i> {operatorLabel}
            </div>
        } else {
            return <div>
                Input <b><WrappedValue>{data.sel}</WrappedValue></b> at <i><WrappedValue>{data.url}</WrappedValue></i> {operatorLabel} {exp.op != 'empty' && exp.op != 'empty_not' ? val : ''}
            </div>
        }
    } else if (attrConfig.value && attrConfig.value.startsWith('surv.')) {
        return <div>Survey <b>{!surveyBare.survey ? '...' : surveyBare.survey?.internalName}</b> {operatorLabel} <b>{attrLabel}</b></div>
    } else {
        const attrLabelHasWeight = attrConfig.hasValue !== false;
        const attrPostValue = !attrLabelHasWeight ? attrId : ''

        return <div>
            {attrLabelHasWeight ? <b>{attrLabel}</b> : <span>{attrLabel}</span>}
            <span> {operatorLabel} </span> {/* keep spaces around! */}
            {typeof val === 'undefined' || val == '' || val == null ? <i>not set</i> : <WrappedValue>{val}</WrappedValue>}
            {operatorPostfixLabel && <span> {operatorPostfixLabel}</span>}
        </div>
    }
}

function WrappedValue(props: { children: React.ReactNode }) {
    return <span style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{props.children}</span>
}

function getRuleSummary(rule: RuleDescriptor, predefinedProps: Array<PredefinedPropType>) {
    const exp = rule.expression;

    const op = exp.op;
    const val = exp.val;
    const prop = exp.prop || '';

    let propName = prop;
    let opName = op;
    let opInputPostFix = '';

    const genOperator = generalOperators.filter(o => o.value == op)[0];
    if (genOperator) {
        opName = genOperator.text;
    }

    const predefinedProp = predefinedProps.filter(i => i.value == prop)[0];
    if (predefinedProp) {
        propName = predefinedProp.displayText || predefinedProp.text;

        if (predefinedProp.operators) {
            const pOp = predefinedProp.operators.filter(i => i.value == op)[0];

            if (pOp) {
                opName = pOp.displayText || pOp.text;
                opInputPostFix = pOp.inputPostfix;
            }
        }
    }

    let valName = stripHtml(val);

    if (prop.startsWith('tour.')) {
        const toursList = getServerSideData('tours') as Array<any>;
        const tour = toursList.filter(t => t.id == val)[0];

        if (tour) {
            valName = tour.name;

            return `<b>${valName}</b> ${opName} ${propName}`;
        }
    }

    if (opInputPostFix) {
        return `${propName} ${opName} ${formatVal(valName)} ${opInputPostFix}`;
    } else {
        return `${propName} ${opName} ${formatVal(valName)}`;
    }

    function formatVal(v) {
        if (v === '' || typeof v === 'undefined' || v === null) {
            return `<i>empty</i>`
        } else {
            return `<b>${v}</b>`
        }
    }
}